import { createContext, useState } from "react";
import UIkit from "uikit";

import ChecklistsService from "../services/checklists.service";

export const ChecklistsContext = createContext();

export const ChecklistsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [checklists, setChecklists] = useState([]);
  const [checklistsCount, setChecklistsCount] = useState(0);
  const [pagination, setPagination] = useState({
    page: 0, // 0-indexed to match backend
    per_page: 10, // Default records per page
  });

  const contextChecklists = {
    loading,
    isProcessing,
    error,
    checklists,
    checklistsCount,
    pagination,

    // Helper functions for pagination control
    setPage: (page) => setPagination((prev) => ({ ...prev, page })),
    setPerPage: (per_page) => setPagination((prev) => ({ ...prev, per_page, page: 0 })),

    getChecklists: async (criteria = {}) => {
      setLoading(true);
      setError(null);
      try {
        // Merge criteria with current pagination state
        const params = {
          page: criteria.page ?? pagination.page,
          per_page: criteria.per_page ?? pagination.per_page,
          ...criteria,
        };

        const response = await ChecklistsService.list(params);
        setChecklists(response.data?.checklists || []);
        setChecklistsCount(response.data?.total_rows || 0);
        
        // Update pagination state with the values used in the request
        setPagination({
          page: params.page,
          per_page: params.per_page,
        });

        setLoading(false);
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.message || "Failed to load checklists";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        setLoading(false);
        throw e;
      }
    },

    createChecklist: async (checklist) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await ChecklistsService.create(checklist);
        if (!response.error) {
          UIkit.notification("Checklist created successfully", { status: "success" });
          
          // Navigate to first page to see the new item in correct sort order
          await contextChecklists.getChecklists({ page: 0, per_page: pagination.per_page });
        } else {
          const errorMessage = response.data?.message || "Error creating checklist";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
        }
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.message || "Error creating checklist";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },

    getChecklist: async (checklistId) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await ChecklistsService.get(checklistId);
        if (!response.error) {
          return response.data;
        } else {
          const errorMessage = response.data?.message || "Error fetching checklist";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
          throw new Error(errorMessage);
        }
      } catch (e) {
        const errorMessage = e.response?.data?.error || "Error fetching checklist";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },

    editChecklist: async (checklistId, data) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await ChecklistsService.update(checklistId, data);
        if (!response.error) {
          UIkit.notification("Checklist updated successfully", { status: "success" });
          
          // Refetch current page to get updated list data with correct item_count
          await contextChecklists.getChecklists({ page: pagination.page, per_page: pagination.per_page });
        } else {
          const errorMessage = response.data?.message || "Error updating checklist";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
        }
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.message || "Error updating checklist";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },

    removeChecklist: async (checklistId) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await ChecklistsService.delete(checklistId);
        if (!response.error) {
          UIkit.notification("Checklist deleted successfully", { status: "success" });
          
          await contextChecklists.getChecklists({ page: pagination.page, per_page: pagination.per_page });
        } else {
          const errorMessage = response.data?.message || "Error deleting checklist";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
        }
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.message || "Error deleting checklist";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },

    cloneChecklist: async (checklistId) => {
      setProcessing(true);
      setError(null);
      try {
        const response = await ChecklistsService.clone(checklistId);
        if (!response.error) {
          UIkit.notification("Checklist cloned successfully", { status: "success" });
          
          await contextChecklists.getChecklists({ page: pagination.page, per_page: pagination.per_page });
        } else {
          const errorMessage = response.data?.message || "Error cloning checklist";
          setError(errorMessage);
          UIkit.notification(errorMessage, { status: "danger" });
        }
        return response.data;
      } catch (e) {
        const errorMessage = e.response?.data?.message || "Error cloning checklist";
        setError(errorMessage);
        UIkit.notification(errorMessage, { status: "danger" });
        throw e;
      } finally {
        setProcessing(false);
      }
    },
  };

  return (
    <ChecklistsContext.Provider value={contextChecklists}>
      {children}
    </ChecklistsContext.Provider>
  );
};
